import { makeStyles } from '@material-ui/core';
import { addMiddleware, isStateTreeNode } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';

import { useInterval } from 'src/common/hooks/useInterval';
import { NAV_HEIGHT } from 'src/components/app/Nav/Nav';
import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { LegacyTopBar } from 'src/myDayToday/LegacyTopBar';
import { MainFocusArea } from 'src/myDayToday/MainFocusArea';
import { SectionErrorBoundary } from 'src/myDayToday/components/SectionErrorBoundary';
import { MDT_REFRESH_INTERVAL } from 'src/myDayToday/constants';
import { generatePopInEvent } from 'src/myDayToday/domain/generatePopInEvent';
import { shouldRenderEventCard } from 'src/myDayToday/domain/shouldRenderEventCard';
import { useMDTEventCards } from 'src/myDayToday/domain/useMDTEventCards';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { LegacyScheduleWidget } from 'src/myDayToday/legacy/LegacyScheduleWidget';
import { useObservability } from 'src/observability/useObservability';
import { Provider } from 'src/shared/stores/resource';
import useRootStore from 'src/stores/useRootStore';
import { ProviderRole } from 'src/stores/users/userType';
import { BoulderColors } from 'src/util/brand';

export const LegacyMyDayToday = () => {
  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);

  const sendStaffEvent = useObservability(enableObservability);

  const styles = useStyles();
  const providerId = useProviderId();
  const rootStore = useRootStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  useInterval(() => setCurrentTime(new Date()), MDT_REFRESH_INTERVAL);

  const { data, error, mutate, isLoading } = useMDTEventCards(providerId);

  const { nextEvent } = data?.staff_myDayTodayEventCards ?? {};
  const teamRole = (rootStore.auth.user as Provider)?.teamRole as ProviderRole;

  const popinEvent =
    !error && nextEvent && shouldRenderEventCard(nextEvent, teamRole)
      ? generatePopInEvent(nextEvent)
      : null;

  useEffect(
    () =>
      sendStaffEvent({
        eventType: 'myDayToday:pageViewed',
        userId: providerId ?? null,
      }),
    [providerId, sendStaffEvent],
  );

  // Refetch the MDT card data when an event has been updated
  // so we can update the display of any newly assigned pop-in
  // event on the calendar widget
  useEffect(() => {
    if (!rootStore || !isStateTreeNode(rootStore)) return () => {};

    const dispose = addMiddleware(
      rootStore,
      (action, next) => {
        if (action.name === 'eventChanged') {
          setImmediate(() => mutate());
        }

        return next(action);
      },
      true,
    );

    return dispose;
  }, [rootStore, mutate]);

  if (!providerId) {
    return <div className={styles.container}>No provider ID found</div>;
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <SectionErrorBoundary sectionName="Top Bar">
          <LegacyTopBar
            currentTime={currentTime}
            avgWaitTime={data?.staff_getAvgWaitTime.avgWaitTime}
            eventCardsData={data?.staff_myDayTodayEventCards}
          />
        </SectionErrorBoundary>
        <div className={styles.bottom}>
          <SectionErrorBoundary sectionName="Schedule Widget">
            <LegacyScheduleWidget
              currentTime={currentTime}
              providerId={providerId}
              popinEvent={popinEvent}
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary sectionName="Main Focus Area">
            <MainFocusArea
              currentTime={currentTime}
              data={data}
              error={error}
              isLoading={isLoading}
              mutate={mutate}
              providerId={providerId}
            />
          </SectionErrorBoundary>
        </div>
      </div>
    </main>
  );
};

const useStyles = makeStyles({
  main: {
    height: `calc(100vh - ${NAV_HEIGHT}px)`,
    backgroundColor: BoulderColors.Gray1,
    overflow: 'auto',
  },
  container: {
    boxSizing: 'border-box',
    fontFamily: '"Nunito", "Nunito Sans"',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  bottom: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: '100%',
    padding: 24,
    gap: 51,
  },
});
